/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { type Registry as Services, service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import {
  type AirbnbGuestDataException,
  type AirbnbGuestDataStep,
  EXCEPTIONS_BY_STEP,
} from 'uplisting-frontend/services/airbnb-guest-data-policy';

interface AirbnbGuestDataAcknowledgementSignature {
  Element: HTMLDivElement;

  Args: {
    step: AirbnbGuestDataStep;
  };
}

export default class UiModalAirbnbGuestDataAcknowledgementComponent extends Component<AirbnbGuestDataAcknowledgementSignature> {
  @service notifications!: Services['notifications'];

  @service('repositories/airbnb-guest-data-acknowledgement')
  airbnbGuestDataAcknowledgementRepository!: Services['repositories/airbnb-guest-data-acknowledgement'];

  @service('airbnb-guest-data-policy')
  policy!: Services['airbnb-guest-data-policy'];

  @tracked exception?: AirbnbGuestDataException;
  @tracked reference = '';
  @tracked isAcknowledged = false;
  @tracked isSaving = false;

  policyUrl = 'https://www.airbnb.com/help/article/465';
  questionId = `${guidFor(this)}-question`;

  get step(): AirbnbGuestDataStep {
    return this.args.step;
  }

  get exceptions(): AirbnbGuestDataException[] {
    return EXCEPTIONS_BY_STEP[this.step];
  }

  get canSubmit(): boolean {
    return !!this.exception && this.isAcknowledged && !this.isSaving;
  }

  @action
  handleSelectException(exception: AirbnbGuestDataException): void {
    this.exception = exception;
  }

  @action
  handleReferenceInput(event: Event): void {
    this.reference = (event.target as HTMLInputElement).value;
  }

  @action
  handleToggleAcknowledged(): void {
    this.isAcknowledged = !this.isAcknowledged;
  }

  @action
  handleCancel(): boolean {
    if (this.isSaving) {
      return false;
    }

    this.policy.cancel();

    return true;
  }

  @action
  @waitFor
  async handleSubmit(): Promise<void> {
    const { step, exception } = this;
    const entry = this.policy.pending;

    if (!entry || !exception || !this.canSubmit) {
      return;
    }

    this.isSaving = true;

    const acknowledgement =
      this.airbnbGuestDataAcknowledgementRepository.createRecord({
        step,
        exception,
        reference: this.reference.trim() || null,
      });

    try {
      await this.airbnbGuestDataAcknowledgementRepository.save(acknowledgement);
    } catch {
      acknowledgement.unloadRecord();
      this.notifications.error();
      this.isSaving = false;

      return;
    }

    this.isSaving = false;

    await this.policy.proceed(entry);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Modal::AirbnbGuestDataAcknowledgement': typeof UiModalAirbnbGuestDataAcknowledgementComponent;
  }
}
